import BaseApiClient from '../BaseApiClient';
import { ListDataParams, ListDataResponse } from '../types';
import TeamInterface, {
  TeamData,
  TeamMemberApiData,
  PipelineTeamTrackingUserRow
} from '@interfaces/Team.interface';
import TeamUserInterface from '@interfaces/TeamUser.interface';
import { UserInterface } from '@interfaces/User.interface';
import {
  AutoCompleteItemResolver,
  AutoCompleteResolver
} from '@ui/AutoCompleteField/types';

export class ApiTeam extends BaseApiClient {
  /**
   * Gets all items
   */
  async all(): Promise<Array<TeamInterface>> {
    return new Promise((resolve, reject) => {
      this.client
        .get('/team?page[size]=9999')
        .then((res) => {
          if (res.data) {
            resolve(res.data.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Get list of items
   */
  async list(
    listData: ListDataParams,
    searchTerm?: string
  ): Promise<ListDataResponse<TeamInterface>> {
    return new Promise((resolve, reject) => {
      if (searchTerm) {
        if (!listData.filters) listData.filters = {};
        listData.filters.name = searchTerm;
      }
      this.client
        .getList('/team', listData)
        .then((res) => {
          if (res.data) {
            resolve({
              items: res.data.data,
              pagination: {
                page: res.data.meta?.page,
                perPage: res.data.meta?.per_page,
                total: res.data.meta?.total
              }
            });
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async getTeamMembers(teamId: number | string): Promise<TeamData[]> {
    return new Promise((resolve, reject) => {
      this.client
        .get(`/api/v1/teams/member/${teamId}`)
        .then((res) => {
          if (res.data) {
            resolve(res.data.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Get pipeline teams
   */
  async getPipelineTeams(
    pipelineId: number | string
  ): Promise<PipelineTeamTrackingUserRow[]> {
    return new Promise((resolve, reject) => {
      this.client
        .get(`/pipelines/${pipelineId}/targets/users`)
        .then((res) => {
          if (res.data) {
            resolve(res.data?.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async getTeamMember(
    teamId: number | string,
    listData: ListDataParams,
    searchTerm?: string
  ): Promise<ListDataResponse<TeamData>> {
    return new Promise((resolve, reject) => {
      if (searchTerm) {
        if (!listData?.filters) listData.filters = {};
        listData.filters.name = searchTerm;
      }
      this.client
        .getList(`/teams/${teamId}/users`, listData)
        .then((res) => {
          if (res.data) {
            resolve({
              items: res.data.data,
              pagination: {
                page: res.data.meta?.current_page,
                perPage: res.data.meta?.per_page,
                total: res.data.meta?.total
              }
            });
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }
  /**
   * Create a team
   */
  async createTeam(data: { name: string }): Promise<TeamInterface> {
    return new Promise((resolve, reject) => {
      this.client
        .post('/api/v1/teams', data)
        .then((res) => {
          if (res.data) resolve(res.data.data);
          else reject('Invalid response');
        })
        .catch(reject);
    });
  }

  async deleteTeam(teamId: number): Promise<void> {
    return new Promise((resolve, reject) => {
      this.client
        .delete(`/api/v1/teams/${teamId}`)
        .then((res) => {
          resolve(res.data);
        })
        .catch(reject);
    });
  }

  async updateTeam(
    team_id: number | undefined,
    data: { name: string }
  ): Promise<TeamInterface> {
    return new Promise((resolve, reject) => {
      this.client
        .put(`/api/v1/teams/${team_id}`, data)
        .then((res) => {
          if (res.data) resolve(res.data.data);
          else reject('Invalid response');
        })
        .catch(reject);
    });
  }

  /**
   * Get a team
   */
  async get(id: number): Promise<TeamInterface> {
    return new Promise((resolve, reject) => {
      this.client
        .get(`/team/${id}`)
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Get team users by team id
   */
  async getTeamTeamUsers(teamId: number): Promise<Array<TeamUserInterface>> {
    return new Promise((resolve, reject) => {
      this.client
        .get(`/team/${teamId}/team-user`)
        .then((res) => {
          if (res.data) {
            resolve(res.data.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Get users of team
   */
  async getTeamMembersUsers(teamId: number): Promise<Array<UserInterface>> {
    return new Promise((resolve, reject) => {
      this.client
        .get(`/team/${teamId}/user?page[size]=999&filter[status]=0`)
        .then((res) => {
          if (res.data) {
            resolve(res.data.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async getTeamMemberBySearch(searchTerm: string): Promise<Array<unknown>> {
    return new Promise((resolve, reject) => {
      this.client
        .get(`/me/team/user?filter[name]=${searchTerm}&page[size]=100`)
        .then((res) => {
          if (res.data) resolve([res.data].flat());
          else reject('Invalid response');
        })
        .catch(reject);
    });
  }

  async getTeamMemberBySearchList(
    listData: ListDataParams,
    searchTerm?: string
  ): Promise<ListDataResponse<any>> {
    return new Promise((resolve, reject) => {
      if (searchTerm) {
        if (!listData.filters) listData.filters = {};
        listData.filters.name = searchTerm;
      }
      this.client
        .getList('/me/team/user', listData)
        .then((res) => {
          if (res.data) {
            resolve({
              items: res.data.data,
              pagination: {
                page: res.data.meta?.current_page,
                perPage: res.data.meta?.per_page,
                total: res.data.meta?.total
              }
            });
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Resolver for autocomplete field
   */
  autoCompleteResolver: AutoCompleteResolver = async (
    searchTerm,
    page,
    perPage
  ) => {
    const data = await this.list({ perPage, page }, searchTerm || undefined);
    return data.items.map((item) => ({
      value: item.id,
      label: item.name
    }));
  };
  /**
   * Item Resolver for autocomplete field
   */
  autoCompleteItemResolver: AutoCompleteItemResolver = async (value) => {
    const item = await this.get(Number(value));
    return {
      value: item.id,
      label: item.name
    };
  };

  async addTeamMember(
    teamId: string,
    data: {
      name?: string | null;
      last_name?: string | null;
      team_id: number | string;
      email?: string | null;
      role_id?: number | string | null;
      user_id?: number | string | null;
    }
  ): Promise<TeamData> {
    return new Promise((resolve, reject) => {
      this.client
        .post(`/teams/${teamId}/users`, data)
        .then((res) => {
          if (res.data) {
            resolve(res.data.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }
  async updateTeamMember(
    teamId: string,
    data: {
      name: string;
      last_name: string;
      team_id: number | string;
      email?: string | null;
      role_id?: number | string | null;
      user_id?: number | string | undefined;
    }
  ): Promise<TeamData> {
    return new Promise((resolve, reject) => {
      this.client
        .put(`/teams/${teamId}/users/${data?.user_id}`, data)
        .then((res) => {
          if (res.data) {
            resolve(res.data.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async unlink(
    teamMemberId?: number | string,
    teamId?: number | string,
    data?: { confirm: boolean }
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      this.client
        .delete(`/teams/${teamId}/users/${teamMemberId}`, {
          data: data
        })
        .then((res) => {
          if (res.status === 204) {
            resolve();
          } else {
            reject('Invalid response');
          }
        })
        .catch(reject);
    });
  }

  async unlinkInvitation(
    teamId?: number | string,
    email?: number | string,
    data?: { confirm: boolean }
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      this.client
        .delete(`/teams/${teamId}/invitations/${email}`, {
          data: data
        })
        .then((res) => {
          if (res.status === 204) {
            resolve();
          } else {
            reject('Invalid response');
          }
        })
        .catch(reject);
    });
  }

  async getUsersTeams(): Promise<Array<TeamMemberApiData>> {
    return new Promise((resolve, reject) => {
      this.client.get(`/users/team`).then((res) => {
        resolve(res.data);
      });
    });
  }

  /**
   * Teams the authenticated user belongs to (join on team_users).
   */
  async getMyTeams(): Promise<Array<TeamInterface>> {
    return new Promise((resolve, reject) => {
      this.client
        .getList('/me/team', { page: 1, perPage: 100 })
        .then((res) => {
          if (res.data?.data) {
            resolve(res.data.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }
  /**
   * Save team tracking data for a pipeline
   */
  async saveTeamTracking(
    pipelineId: number,
    data: {
      data: Array<{
        user_id?: number;
        target_revenue: number;
        monthly_targets?: Array<{
          month: string;
          target_revenue: number;
        }>;
      }>;
      target_date: string;
    }
  ): Promise<{ success: boolean; message: string }> {
    return new Promise((resolve, reject) => {
      this.client
        .put(`/pipelines/${pipelineId}/users`, data)
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }
}

export { teamQueryKeys } from './query-keys';
export type {
  TeamListQueryParams,
  TeamMemberListQueryParams,
  TeamMemberSearchListQueryParams
} from './query-keys';
export {
  useTeamAllQuery,
  useTeamListQuery,
  useTeamMemberBySearchQuery,
  useTeamMemberListQuery,
  useTeamMemberSearchListQuery,
  useTeamMembersQuery,
  useTeamMembersUsersQuery,
  useTeamMyTeamsQuery,
  useTeamPipelineTeamsQuery,
  useTeamQuery,
  useTeamTeamUsersQuery,
  useTeamUsersTeamsQuery
} from './use-team-queries';
export {
  useTeamAddMemberMutation,
  useTeamCreateTeamMutation,
  useTeamDeleteTeamMutation,
  useTeamSaveTrackingMutation,
  useTeamUnlinkInvitationMutation,
  useTeamUnlinkMutation,
  useTeamUpdateMemberMutation,
  useTeamUpdateTeamMutation
} from './use-team-mutations';
export type {
  TeamAddMemberVariables,
  TeamSaveTrackingVariables,
  TeamUnlinkInvitationVariables,
  TeamUnlinkVariables,
  TeamUpdateMemberVariables,
  TeamUpdateTeamVariables
} from './use-team-mutations';
